import React from 'react';
import { useNavigate } from 'react-router-dom';
import IReservation, { EReservationStatus } from '../../shared/IReservation';
import Button, { EButtonColor } from '../ui/Button/Button';
import Alert, { EAlertColor } from '../ui/Alert/Alert';
import { useReservationCancelModal } from './ReservationCancelModal/useReservationCancelModal';
import { Route } from '../../routes';
import "./ReservationEdit.scss";

interface IProps {
  reservation: IReservation;
}

const ReservationEdit: React.FC<IProps> = ({ reservation }) => {
  const getFallbackExtraName = (code: string) => {
    const fallbacks: Record<string, string> = {
      'RTPO': 'Road toll',
      'RTPN': 'Road toll North',
      'BSC': 'Base charge',
      'STS': 'Station Supplement'
    };
    return fallbacks[code] || '';
  };

  const navigate = useNavigate();
  const openCancelModal = useReservationCancelModal();

  const isStatusOk = [
    EReservationStatus.RES,
    EReservationStatus.OK,
    EReservationStatus.SENDING,
  ].includes(reservation.status as EReservationStatus);

  const onPrint = () => {
    window.print();
  };

  const onPrintCustomer = () => {
    const el = document.getElementById("reservation-edit");
    el?.classList.add("hide-prices-print");
    window.print();
    el?.classList.remove("hide-prices-print");
  };

  const onCancel = async () => {
    const CANCEL_ALLOWED_BEFORE = 0;
    
    if (reservation.daysFromNow !== undefined && reservation.daysFromNow <= CANCEL_ALLOWED_BEFORE) {
      alert('Reservation must be canceled at least 30 days prior to pick up date.');
      return;
    }

    const canceled = await openCancelModal(reservation);
    if (canceled) {
      navigate(Route.reservations);
    }
  };

  const formatCurrency = (amount: number) => {
    return `${(amount / 100).toFixed(2)} ${reservation.currency}`;
  };

  return (
    <section id="reservation-edit">
      {/* Header / Actions */}
      <div className="header-actions">
        <div className="title-area">
          <h1>Reservation {reservation.refNo} / IRN {reservation.irn}</h1>
          {isStatusOk && (
            <p>
              Remember to print out the <strong>customer voucher</strong> and provide a copy for the clients to present at the rental station.
            </p>
          )}
        </div>
        {isStatusOk && 
          <div className="buttons">
            <Button onClick={onPrint} color={EButtonColor.PRIMARY}>
              Print Agent Voucher
            </Button>
            <Button onClick={onPrintCustomer} color={EButtonColor.PRIMARY}>
              Print Customer Voucher
            </Button>
            <Button onClick={onCancel} color={EButtonColor.DANGER}>
              Cancel reservation
            </Button>
          </div>
        }
      </div>

      {/* Legacy Print Logo */}
      <div className="visible-print-block">
        <div className="print-header">
          <div className="print-header-left">
            <img src="/images/touringcars_logo.jpg" alt="Touring Cars" className="print-logo" />
            {reservation.departureStation && (
              <div className="print-business-details">
                <strong>{reservation.departureCode} {reservation.departureName}</strong><br />
                {reservation.departureStation.address}<br />
                {reservation.departureStation.zip} {reservation.departureStation.city}<br />
                {reservation.departureStation.country && reservation.departureStation.country.toUpperCase()}
              </div>
            )}
          </div>
          <div className="print-header-right">
            <strong>Reservation No.:</strong><br />
            <span className="ref-no">{reservation.refNo}</span><br />
            <span className="irn-no">irn {reservation.irn}</span>
          </div>
        </div>
      </div>

      {/* Status Panels */}
      {!isStatusOk && (
        <div className="status-panels">
          {reservation.status === EReservationStatus.CNC ? (
            <Alert color={EAlertColor.DANGER} className="compact-status-alert">
              <div className="alert-content">
                <div className="alert-title">Canceled reservation</div>
                <div className="alert-details">
                  <div className="detail-item">
                    <span className="label">Updated:</span>
                    <span className="value">{reservation.updated}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Status:</span>
                    <span className="value">{reservation.status}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Reason:</span>
                    <span className="value">{reservation.reasonToCancel || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </Alert>
          ) : reservation.status === EReservationStatus.REQ ? (
            <Alert color={EAlertColor.PRIMARY} className="compact-status-alert">
              <div className="alert-content">
                <div className="alert-title">Requested reservation</div>
                <p>
                  The reservation is not confirmed, please contact sales support at agents@touringcars.eu
                </p>
              </div>
            </Alert>
          ) : !reservation.status ? (
            <Alert color={EAlertColor.DANGER} className="compact-status-alert">
              <div className="alert-content">
                <div className="alert-title">Reservation not found</div>
                <p>Error: The reservation was not found</p>
              </div>
            </Alert>
          ) : (
             <Alert color={EAlertColor.DANGER} className="compact-status-alert">
              <div className="alert-content">
                <div className="alert-title">Reservation status: {reservation.status}</div>
              </div>
            </Alert>
          )}
        </div>
      )}

      <h3 className="voucher-title">Car Rental Voucher</h3>

      <div className="row-layout">
        <div className="col-half">
          <div className="sub-title">Meeting Point:</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td>
                  {reservation.pickupInfo ? (
                    <div>{reservation.pickupInfo}</div>
                  ) : reservation.meetingPoint === 'AIRPORT' ? (
                    <div>
                      Airport<br />
                      {reservation.meetingPointAirport === 'FLIGHT' ? (
                        <>
                          {reservation.meetingPointFlightNumber && `Flight ${reservation.meetingPointFlightNumber}`}
                          {reservation.meetingPointEta && ` arriving ${reservation.meetingPointEta}`}
                        </>
                      ) : (
                        <>
                          {reservation.meetingPointTerminal && `Terminal ${reservation.meetingPointTerminal}`}
                        </>
                      )}
                    </div>
                  ) : (
                    <div>Rental station</div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col-half">
          <div className="sub-title">Reservation No.:</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td>
                  {reservation.refNo}<br />
                  IRN {reservation.irn}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="row-layout">
        <div className="col-half">
          <div className="sub-title">Pick Up Details</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">Pick Up At:</td>
                <td className="value">{reservation.departureCode} {reservation.departureName}</td>
              </tr>
              <tr>
                <td className="label">Rental Start:</td>
                <td className="value">{reservation.dateFrom} {reservation.timeFrom}</td>
              </tr>
              <tr>
                <td className="label">Address:</td>
                <td>
                  <address>
                    {reservation.departureStation.address}<br />
                    {reservation.departureStation.zip} {reservation.departureStation.city}
                  </address>
                </td>
              </tr>
              <tr>
                <td className="label">Telephone:</td>
                <td className="value">{reservation.departureStation.phoneNumber}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col-half">
          <div className="sub-title">Return Details</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">Return To:</td>
                <td className="value">{reservation.returnCode} {reservation.returnName}</td>
              </tr>
              <tr>
                <td className="label">Rental End:</td>
                <td className="value">{reservation.dateTo} {reservation.timeTo}</td>
              </tr>
              <tr>
                <td className="label">Address:</td>
                <td>
                  <address>
                    {reservation.returnStation.address}<br />
                    {reservation.returnStation.zip} {reservation.returnStation.city}
                  </address>
                </td>
              </tr>
              <tr>
                <td className="label">Telephone:</td>
                <td className="value">{reservation.returnStation.phoneNumber}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="row-layout">
        <div className="col-half">
          <div className="sub-title">Your Details</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">Driver's Name:</td>
                <td className="value">{reservation.customerName}</td>
              </tr>
              <tr>
                <td className="label">Email:</td>
                <td className="value">{reservation.customerEmail}</td>
              </tr>
              <tr>
                <td className="label">Telephone:</td>
                <td className="value">{reservation.customerPhone}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="col-half">
          <div className="sub-title">Vehicle Details</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">Booked category:</td>
                <td className="value">{reservation.group} {reservation.vehicleName}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="row-layout">
        <div className="col-half col-stack-print">
          <div className="sub-title">Special Equipment</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Info</th>
              </tr>
            </thead>
            <tbody>
              {reservation.reservationExtras
                ?.sort((a, b) => a.code.localeCompare(b.code))
                .map((extra) => (
                  <tr key={extra.code}>
                    <td>{extra.code}</td>
                    <td>{extra.name || getFallbackExtraName(extra.code)}</td>
                    <td>{extra.amount}</td>
                    <td>
                      {extra.extraInfo?.map((info, idx) => (
                        <div key={idx}>
                          {info.age && `Age: ${info.age}`} {info.height && `Height: ${info.height}`}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="col-half hide-price col-stack-print">
          <div className="sub-title">Charges Summary</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Service or Surcharge Description</th>
                <th>Qty</th>
                <th>Unit Value</th>
                <th>Charge</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{reservation.group}</td>
                <td>{reservation.vehicleName}</td>
                <td>1</td>
                <td>{formatCurrency(reservation.vehicleRate)}</td>
                <td>{formatCurrency(reservation.vehicleRate)}</td>
              </tr>
              {reservation.outOfHours > 0 && (
                <tr>
                  <td></td>
                  <td>incl. out of office hours charge</td>
                  <td>1</td>
                  <td>{formatCurrency(reservation.outOfHours)}</td>
                  <td>{formatCurrency(reservation.outOfHours)}</td>
                </tr>
              )}
              {reservation.oneway > 0 && (
                <tr>
                  <td></td>
                  <td>One-way rental charge</td>
                  <td>1</td>
                  <td>{formatCurrency(reservation.oneway)}</td>
                  <td>{formatCurrency(reservation.oneway)}</td>
                </tr>
              )}
              {reservation.reservationExtras
                ?.sort((a, b) => a.code.localeCompare(b.code))
                .map((extra) => (
                  <tr key={`${extra.code}-charge`}>
                    <td>{extra.code}</td>
                    <td>{extra.name || getFallbackExtraName(extra.code)}</td>
                    <td>{extra.amount}</td>
                    <td>{formatCurrency(extra.rate)}</td>
                    <td>{formatCurrency(extra.rate * extra.amount)}</td>
                  </tr>
                ))}
              <tr className="total-row">
                <td colSpan={4}>Total charge</td>
                <td>{formatCurrency(reservation.totalRate)}</td>
              </tr>
              <tr>
                <td colSpan={5}>All prices include VAT</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="policy-section">
        <div className="policy-main">
          <div className="print-break-avoid">
            <div className="sub-title">Rental amount payable</div>
            <p>
              The completion of the full payment for the booking is due 30 days prior to pickup. Moreover for bookings made closer than 30 days to pickup, the full payment is due immediately. Reservation is fully confirmed only after the full payment is received. Any additional item and service, or supplementary insurance, purchased locally, are payable at the rental desk. Please, take into consideration that any deviation from the confirmed details, such as a change of dates, car type, adding any option or different pickup / return times could incur extra charges or even a closed desk / office.
            </p>
          </div>

          <div className="print-break-avoid">
            <div className="sub-title" style={{ marginTop: '1.5rem' }}>Credit Card Authorization and Driving License</div>
            <p>
              A valid credit card and driving license must be presented upon pickup of the Motorhome. A deposit of funds is reserved upon pickup to ensure traffic fines, missing fuel / refilling service, a safe return of the car, extending the rental a few days and/or, in some cases, for damages. The reserved funds (deposit) will not be charged on the credit card, but also will not be available on the account until a few days after the car is returned.
            </p>
          </div>
        </div>

        <div className="policy-side">
          <div className="print-break-avoid">
            <p><strong>Cancellation policy</strong></p>
            <p>
              30 days or more to arrival date: free cancellation
              <br />
              29 days to 22 days prior to departure: 25%
              <br />
              21 days to 16 days prior to departure: 50%
              <br />
              15 days to 1 day prior departure: 100%
              <br />
              Departure day or no-show: 100%
              <br />
              <br />
              If Cancellation protection is purchased, the cancellation policy is according to the Cancellation protection’s terms and conditions.
            </p>
          </div>
        </div>
      </div>

      <div className="footer-meta">
        <small>{reservation.irn} {reservation.created}</small>
      </div>
    </section>
  );
};

export default ReservationEdit;
